// =====================================================
// 白板系统 - 标签栏 UI 组件
// 浏览器风格的标签栏：支持拖拽排序、关闭、右键菜单、新建与未保存状态
// =====================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, LayoutGrid, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWhiteboardTabsStore } from '../stores/whiteboard-tabs-store';
import { useWhiteboardStore } from '../stores/whiteboard-store';
import type { WhiteboardTab } from '../stores/whiteboard-tabs-store';

// =====================================================
// 右键菜单
// =====================================================
function TabContextMenu({
  position,
  hasRightTabs,
  onClose,
  onCloseTab,
  onCloseOthers,
  onCloseRight,
  onCloseAll,
}: {
  position: { x: number; y: number };
  hasRightTabs: boolean;
  onClose: () => void;
  onCloseTab: () => void;
  onCloseOthers: () => void;
  onCloseRight: () => void;
  onCloseAll: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // 调整位置，防止溢出视口
  const [adjustedPos, setAdjustedPos] = useState(position);
  useEffect(() => {
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    let { x, y } = position;
    if (x + rect.width > window.innerWidth) x = window.innerWidth - rect.width - 8;
    if (y + rect.height > window.innerHeight) y = window.innerHeight - rect.height - 8;
    setAdjustedPos({ x, y });
  }, [position]);

  const items = [
    { label: '关闭标签页', onClick: onCloseTab, disabled: false },
    { label: '关闭其他标签页', onClick: onCloseOthers, disabled: false },
    { label: '关闭右侧标签页', onClick: onCloseRight, disabled: !hasRightTabs },
    { label: '关闭所有标签页', onClick: onCloseAll, disabled: false },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px] animate-in fade-in zoom-in-95 duration-100"
      style={{ left: adjustedPos.x, top: adjustedPos.y }}
    >
      {items.map((item) => (
        <button
          key={item.label}
          disabled={item.disabled}
          onClick={() => {
            if (item.disabled) return;
            item.onClick();
            onClose();
          }}
          className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
            item.disabled
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

// =====================================================
// 单个标签页
// =====================================================
function Tab({
  tab,
  index,
  isActive,
  isDirty,
  onActivate,
  onClose,
  onContextMenu,
  onReorder,
}: {
  tab: WhiteboardTab;
  index: number;
  isActive: boolean;
  isDirty: boolean;
  onActivate: () => void;
  onClose: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <div
      draggable
      onDragStart={(e) => {
        setIsDragging(true);
        e.dataTransfer.setData('text/plain', String(index));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => {
        setIsDragging(false);
        setIsDragOver(false);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!isDragOver) setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const fromStr = e.dataTransfer.getData('text/plain');
        if (fromStr !== '') {
          const fromIndex = parseInt(fromStr, 10);
          if (!isNaN(fromIndex)) {
            onReorder(fromIndex, index);
          }
        }
      }}
      onClick={onActivate}
      onMouseDown={(e) => {
        // 鼠标中键直接关闭标签页
        if (e.button === 1) {
          e.preventDefault();
          onClose(e);
        }
      }}
      onContextMenu={onContextMenu}
      className={`
        group relative flex items-center gap-1.5 px-3 h-8 shrink-0
        text-xs font-medium cursor-pointer select-none
        border-r border-gray-200/60 transition-all duration-150
        ${isActive
          ? 'bg-white text-gray-900 shadow-[0_1px_0_white]'
          : 'bg-gray-100/60 text-gray-500 hover:bg-gray-50 hover:text-gray-700'
        }
        ${isDragging ? 'opacity-40' : 'opacity-100'}
        ${isDragOver ? 'border-l-2 border-l-blue-500' : ''}
      `}
      style={{ maxWidth: 180, minWidth: 80 }}
    >
      {/* 激活指示条 */}
      {isActive && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500 rounded-b" />
      )}

      {/* 标签名 */}
      <span className="truncate flex-1" title={tab.name}>
        {tab.name || '未命名白板'}
      </span>

      {/* 关闭按钮与未保存小圆点容器 */}
      <div className="shrink-0 w-4 h-4 flex items-center justify-center relative">
        {/* 未保存指示圆点（VS Code 风格：非悬浮时显示小圆点） */}
        {isDirty && (
          <span
            className="w-2 h-2 rounded-full bg-blue-500 group-hover:hidden transition-all"
            title="有未保存修改"
          />
        )}

        {/* 关闭按钮（有未保存时悬浮展示，普通状态下常驻/悬浮展示） */}
        <button
          onClick={onClose}
          className={`
            w-4 h-4 rounded-sm flex items-center justify-center
            transition-all duration-100
            ${isDirty ? 'hidden group-hover:flex' : ''}
            ${isActive
              ? 'text-gray-400 hover:text-gray-700 hover:bg-gray-200'
              : 'text-transparent group-hover:text-gray-400 hover:!text-gray-700 hover:!bg-gray-200'
            }
          `}
          title="关闭标签页 (支持鼠标中键点击)"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// =====================================================
// 标签栏主组件
// =====================================================
export function WhiteboardTabBar() {
  const navigate = useNavigate();
  const {
    tabs,
    activeTabId,
    dirtyTabs,
    activateTab,
    closeTab,
    closeOtherTabs,
    closeRightTabs,
    closeAllTabs,
    reorderTabs,
    openTab,
  } = useWhiteboardTabsStore();
  const createProject = useWhiteboardStore((s) => s.createProject);

  const [contextMenu, setContextMenu] = useState<{ tab: WhiteboardTab; index: number; x: number; y: number } | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // 检查是否可以滚动
  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(el);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
      };
    }
  }, [checkScroll, tabs.length]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  const handleActivate = (id: string) => {
    activateTab(id);
    navigate(`/whiteboard/${id}`, { replace: true });
  };

  const handleClose = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const nextId = closeTab(id);
    if (nextId) {
      navigate(`/whiteboard/${nextId}`, { replace: true });
    } else {
      navigate('/whiteboard', { replace: true });
    }
  };

  const handleContextMenu = (e: React.MouseEvent, tab: WhiteboardTab, index: number) => {
    e.preventDefault();
    setContextMenu({ tab, index, x: e.clientX, y: e.clientY });
  };

  const handleCloseFromMenu = (tabId: string) => {
    const nextId = closeTab(tabId);
    if (tabId === activeTabId) {
      if (nextId) {
        navigate(`/whiteboard/${nextId}`, { replace: true });
      } else {
        navigate('/whiteboard', { replace: true });
      }
    }
  };

  const handleCloseOthers = (tabId: string) => {
    closeOtherTabs(tabId);
    navigate(`/whiteboard/${tabId}`, { replace: true });
  };

  const handleCloseRight = (tabId: string) => {
    closeRightTabs(tabId);
    // 如果关闭了当前激活的标签，closeRightTabs 会将激活项重置为 tabId
    navigate(`/whiteboard/${tabId}`, { replace: true });
  };

  const handleCloseAll = () => {
    closeAllTabs();
    navigate('/whiteboard', { replace: true });
  };

  // 快速新建白板
  const handleCreateNewTab = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const newProj = await createProject({ name: '未命名白板' });
      openTab(newProj.id, newProj.name);
      navigate(`/whiteboard/${newProj.id}`);
    } catch (error) {
      console.error('[Whiteboard] 新建标签页失败:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // 标签数为 0 时不渲染标签栏
  if (tabs.length === 0) return null;

  return (
    <>
      <div className="flex items-stretch bg-gray-100 border-b border-gray-200 shrink-0 h-8 select-none">
        {/* macOS 原生红绿灯与 Knowledge Hub 标题避让区（宽 290px，标签页整体靠右） */}
        <div
          className="shrink-0 w-[290px] border-r border-gray-200/60 bg-gray-100/90"
          style={{ WebkitAppRegion: 'drag' } as React.CSSProperties}
          title="macOS 操作栏避让区（可按住拖拽窗口）"
        />

        {/* 返回列表按钮 */}
        <button
          onClick={() => navigate('/whiteboard')}
          className="flex items-center gap-1 px-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200/60 transition-colors shrink-0 border-r border-gray-200/60"
          title="所有白板"
        >
          <LayoutGrid className="w-3.5 h-3.5" />
        </button>

        {/* 左滚动箭头 */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="flex items-center px-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 transition-colors shrink-0"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
        )}

        {/* 标签页容器 */}
        <div
          ref={scrollRef}
          className="flex items-stretch flex-1 overflow-x-auto scrollbar-none"
          style={{ scrollbarWidth: 'none' }}
        >
          {tabs.map((tab, idx) => (
            <Tab
              key={tab.id}
              tab={tab}
              index={idx}
              isActive={tab.id === activeTabId}
              isDirty={!!dirtyTabs[tab.id]}
              onActivate={() => handleActivate(tab.id)}
              onClose={(e) => handleClose(e, tab.id)}
              onContextMenu={(e) => handleContextMenu(e, tab, idx)}
              onReorder={reorderTabs}
            />
          ))}

          {/* 快速新建白板按钮 */}
          <button
            onClick={() => void handleCreateNewTab()}
            disabled={isCreating}
            className="flex items-center justify-center w-7 h-8 text-gray-400 hover:text-gray-700 hover:bg-gray-200/60 transition-colors shrink-0"
            title="新建白板标签页"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* 右滚动箭头 */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="flex items-center px-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 transition-colors shrink-0"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* 右键菜单 */}
      {contextMenu && (
        <TabContextMenu
          position={{ x: contextMenu.x, y: contextMenu.y }}
          hasRightTabs={contextMenu.index < tabs.length - 1}
          onClose={() => setContextMenu(null)}
          onCloseTab={() => handleCloseFromMenu(contextMenu.tab.id)}
          onCloseOthers={() => handleCloseOthers(contextMenu.tab.id)}
          onCloseRight={() => handleCloseRight(contextMenu.tab.id)}
          onCloseAll={handleCloseAll}
        />
      )}
    </>
  );
}
